/**
 * Utility Functions
 *
 * @version 2.1.0
 * @note Migrated: require → import, process.env → import.meta.env
 * @note v2.1.0: extractErrorMessage ora gestisce le strutture di validation errors
 *               Laravel ({ field: ["msg", ...] }) sia in error.data (response già
 *               unwrappato) sia in error.response.data / error.response.data.errors.
 *               Traduzione opportunistica via i18n.global.t() — zero rischio di
 *               circular dep (i18n.ts importa solo vue-i18n/axios/locales).
 */

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import i18n from '@/i18n'

dayjs.extend(utc)

export function isNil(value: any): boolean {
  return value === null || value === undefined
}

export function isNullOrEmpty(value: any) {
  return !(typeof value === 'string' && value.trim().length > 0)
}

export function atLeastTwo(a: Boolean, b: Boolean, c: Boolean) {
  return a ? b || c : b && c
}

export function numberToTwoDecimalPlaces(value: any): any {
  return (Math.round(parseFloat(value) * 100) / 100).toFixed(2)
}

/**
 * Translate a backend message via i18n if a key exists, otherwise return as-is.
 * Used opportunistically: backend messages like "The email must be a valid email
 * address." are already keys in `locales/*.json`. Falls back gracefully if the
 * key is missing (vue-i18n returns the key itself).
 */
function translateIfPossible(msg: string): string {
  if (!msg) return msg
  try {
    const translated = i18n.global.t(msg)
    return typeof translated === 'string' ? translated : msg
  } catch {
    return msg
  }
}

/**
 * Recursively flatten a Laravel-style validation errors map into a single string.
 *
 * Accepts:
 *  - `{ email: ["msg1", "msg2"], name: ["msg3"] }`  →  "msg1 — msg3"  (translated)
 *  - `{ message: "..." }`                            →  "..."          (translated)
 *  - `{ error: "..." }`                              →  "..."          (translated)
 *  - `{ errors: { ... } }`                           →  recurse on inner map
 *  - Mixed shapes are tolerated.
 *
 * Returns null if nothing usable was found.
 */
function extractFromValidationMap(data: Record<string, any>): string | null {
  if (data == null || typeof data !== 'object') return null

  // Common single-message shapes first
  if (typeof data.message === 'string' && data.message.trim().length > 0) {
    return translateIfPossible(data.message)
  }
  if (typeof data.error === 'string' && data.error.trim().length > 0) {
    return translateIfPossible(data.error)
  }

  // Laravel FormRequest wraps the per-field map under `errors`
  if (data.errors != null && typeof data.errors === 'object') {
    const recursed = extractFromValidationMap(data.errors)
    if (recursed) return recursed
  }

  // Per-field map: collect first message from each field, take only the first
  // by default; if the caller wants more, can be made configurable later.
  const messages: string[] = []
  for (const key of Object.keys(data)) {
    const value = data[key]
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && item.trim().length > 0) {
          messages.push(translateIfPossible(item))
        }
      }
    } else if (typeof value === 'string' && value.trim().length > 0) {
      messages.push(translateIfPossible(value))
    }
  }

  if (messages.length === 0) return null
  // Join with " — " so multiple field errors are still readable in a snackbar
  return messages.join(' — ')
}

/**
 * Extract a human-readable error message from any error shape.
 *
 * Handles:
 *  - Axios errors:        error.response.data.message
 *  - Axios validation:    error.response.data.errors / error.response.data
 *  - Direct API response: error.data.message / error.data
 *  - Direct validation:   error.data (per-field map)  ← caso comune del progetto
 *  - Native Error:        error.message
 *  - Nested:              error.error.message
 *  - HTTP:                error.statusText
 *  - String:              error itself
 *
 * Messages are translated opportunistically via i18n if a matching key exists
 * in locales/*.json (e.g. "The given data was invalid." → "Dati non corretti").
 *
 * @param error — Axios error, API response, Error object, or string
 * @returns A clean string message, never [object Object]
 */
export function extractErrorMessage(error: any): string {
  if (error == null) return translateIfPossible('Errore sconosciuto')
  if (typeof error === 'string') return translateIfPossible(error)

  // 1) Try the conventional single-message paths first (cheap & fast)
  const directMessage: string | undefined =
    error?.response?.data?.message ??
    error?.data?.message ??
    error?.message ??
    error?.error?.message ??
    (typeof error?.error === 'string' ? error.error : undefined) ??
    error?.statusText

  if (typeof directMessage === 'string' && directMessage.trim().length > 0) {
    const translated = translateIfPossible(directMessage)
    // User-friendly overrides for transport-level errors (translate already done)
    if (translated.includes('Network Error')) return 'Errore di rete: server non raggiungibile'
    if (translated.includes('timeout')) return 'Errore di rete: timeout scaduto'
    return translated
  }

  // 2) Fall back to deep parsing of validation maps.
  //    Two entry points: error.response.data (axios untouched) or error.data
  //    (already-unwrapped response, which is the common case in this project).
  const fromResponseData = extractFromValidationMap(error?.response?.data)
  if (fromResponseData) return fromResponseData

  const fromData = extractFromValidationMap(error?.data)
  if (fromData) return fromData

  return translateIfPossible('Errore sconosciuto')
}

/**
 * Testo da mostrare per un errore catturato: gli store lanciano già una
 * STRINGA (`throw standardizeErrorMessage(error)`), quindi `e?.message`
 * sarebbe undefined e si perderebbe il messaggio parlante del BE (422).
 * Accetta stringa, Error, risposta axios; fallback se vuoto.
 */
export function errorText(e: unknown, fallback = 'Operazione non riuscita'): string {
  if (typeof e === 'string' && e.trim() !== '') return e
  const msg = extractErrorMessage(e)
  return msg && msg !== 'Errore sconosciuto' ? msg : fallback
}

/**
 * @deprecated Use extractErrorMessage() instead.
 * Kept for backward compatibility — now returns a clean string (was returning an object before).
 */
export function standardizeErrorMessage(error: any): string {
  return extractErrorMessage(error)
}

/**
 * Errore API che conserva lo status HTTP (FE v2.173.0).
 *
 * `standardizeErrorMessage` restituisce una stringa e perde lo status: una
 * pagina di dettaglio non puo' distinguere "non esiste" (404) da "non hai i
 * permessi" (403). ApiError resta compatibile con i consumer che si aspettano
 * la stringa: `message` e' il testo gia' estratto/tradotto, `toString()`
 * restituisce il messaggio (interpolazione e `String(e)` invariati) ed
 * `errorText()` / `extractErrorMessage()` lo leggono da `e.message`.
 */
export class ApiError extends Error {
  readonly status: number | null
  readonly code: string | null

  constructor(message: string, status: number | null = null, code: string | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }

  override toString(): string {
    return this.message
  }
}

/** Converte un errore axios (o qualunque errore) in ApiError con status e codice BE. */
export function toApiError(error: any): ApiError {
  if (error instanceof ApiError) return error
  const status: number | null = error?.response?.status ?? error?.status ?? null
  const code: string | null = error?.response?.data?.code ?? error?.data?.code ?? null
  return new ApiError(extractErrorMessage(error), status, code)
}

export function getWeekOfDate(date?: any) {
  const currentDate = date != null ? dayjs(date) : dayjs()
  const weekStart = currentDate.startOf('isoWeek' as any)
  const days = []
  for (let i = 0; i < 6; i++) {
    days.push(weekStart.add(i, 'day'))
  }
  return days
}

export function isAdmin(userRole: string | null | undefined): boolean {
  return userRole === 'admin' || userRole === 'super-admin'
}

export function validateEmail(email: string) {
  const re =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export function getProspectDisplayValue(prospect_details: any): string {
  if (prospect_details == null) {
    return 'Null!'
  }
  var value = ''
  if (!isNullOrEmpty(prospect_details.company_name)) {
    if (!isNullOrEmpty(prospect_details.contact_name) && !isNullOrEmpty(prospect_details.contact_surname)) {
      value = `${prospect_details.company_name} (${prospect_details.contact_name} ${prospect_details.contact_surname})`
    } else if (!isNullOrEmpty(prospect_details.contact_name)) {
      value = `${prospect_details.company_name} (${prospect_details.contact_name})`
    } else if (!isNullOrEmpty(prospect_details.contact_surname)) {
      value = `${prospect_details.company_name} (${prospect_details.contact_surname})`
    } else {
      value = `${prospect_details.company_name}`
    }
  } else {
    if (!isNullOrEmpty(prospect_details.contact_name) && !isNullOrEmpty(prospect_details.contact_surname)) {
      value = `${prospect_details.contact_name} ${prospect_details.contact_surname}`
    } else if (!isNullOrEmpty(prospect_details.contact_name)) {
      value = `${prospect_details.contact_name}`
    } else if (!isNullOrEmpty(prospect_details.contact_surname)) {
      value = `${prospect_details.contact_surname}`
    }
  }
  if (value.length == 0) {
    value = 'Null!'
  }

  return value
}

export function getProspectFullDisplayValue(prospect_details: any): string {
  if (prospect_details == null) {
    return 'Null!'
  }
  var value = getProspectDisplayValue(prospect_details)

  if (!isNullOrEmpty(prospect_details.address)) {
    value += ` - ${prospect_details.address}`
  }

  if (!isNullOrEmpty(prospect_details.cap)) {
    value += ` - ${prospect_details.cap}`
  }

  if (!isNullOrEmpty(prospect_details.city)) {
    value += ` - ${prospect_details.city}`
  }

  if (!isNullOrEmpty(prospect_details.province)) {
    value += ` - ${prospect_details.province}`
  }

  if (!isNullOrEmpty(prospect_details.phone1)) {
    value += ` - ${prospect_details.phone1}`
  }

  if (value.length == 0) {
    value = 'Null!'
  }

  return value
}

export function showFile(pdfdata: any, _filename: string) {
  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  var newBlob = new Blob([pdfdata], { type: 'application/pdf' })

  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
    return
  }

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const data = window.URL.createObjectURL(newBlob)
  var link = document.createElement('a')
  link.href = data
  link.target = '_blank'
  //link.download = filename
  link.click()
  setTimeout(function () {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(data)
  }, 100)
}

export function downloadFile(pdfdata: any, filename: string) {
  // It is necessary to create a new blob object with mime-type explicitly set
  // otherwise only Chrome works like it should
  var newBlob = new Blob([pdfdata], { type: 'application/pdf' })

  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    ;(window.navigator as any).msSaveOrOpenBlob(newBlob)
    return
  }

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const data = window.URL.createObjectURL(newBlob)
  var link = document.createElement('a')
  link.href = data
  link.target = '_blank'
  link.setAttribute('download', filename)
  //link.download = filename
  link.click()
  setTimeout(function () {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(data)
  }, 100)
}

/**
 * Risolve l'URL di un'immagine prodotto a partire dal `path` emesso dal backend.
 *
 * Il backend può restituire:
 *  - un path RELATIVO (es. `/storage/horeca/...` o `/images/products/...`) che va
 *    prefissato col dominio API (`VITE_API_DOMAIN`);
 *  - un URL ASSOLUTO (es. immagine live del fornitore HORECA-FOOD,
 *    `https://tools.electroluxprofessional.com/...`) che va usato così com'è.
 *
 * Centralizza la regola "prefissa solo se relativo" usata da lista/dettaglio/
 * dialog prodotti, evitando di rompere gli URL assoluti dei fornitori.
 */
export function resolveImageUrl(path: string | null | undefined, baseDomain?: string): string {
  if (path == null || path === '') return ''
  if (/^https?:\/\//i.test(path)) return path
  const base = baseDomain ?? (import.meta.env.VITE_API_DOMAIN || '')
  return base + path
}

/**
 * Label di FALLBACK per i codici settore. La fonte di verità è l'anagrafica
 * (`useInterventionsStore().sectorName(cod)` ← GET /api/sectors): questa mappa
 * statica copre solo i render prima del fetch o su store non inizializzato.
 */
export function getAreaFromCodArea(cod_area: string) {
  const fallback: Record<string, string> = {
    M: 'Maglieria',
    L: 'Lavanderia',
    H: 'Horeca',
    HF: 'Horeca Food',
    HL: 'Horeca Laundry',
    U: 'Union',
    F: 'Follatura',
  }
  return fallback[cod_area] ?? cod_area
}

export function getNumberOfPastDaysFrom(date: any): number {
  return dayjs().diff(dayjs.utc(date).local(), 'day')
}
